import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Edit03 } from '@untitledui/icons/Edit03';
import { useMemo, useState } from 'react';

import { AlertFloating } from '@/components/application/alerts/alerts';
import { Avatar } from '@/components/base/avatar/avatar';
import { Badge } from '@/components/base/badges/badges';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { ControlledModal } from '@/components/base/modals/modal';
import { notify } from '@/components/base/notifications/notifications';
import {
  RadioButton,
  RadioGroup,
} from '@/components/base/radio-buttons/radio-buttons';
import { queryKeys } from '@/hooks/api/query-keys';

import type { BrowseTeam } from '../-utils/organization-settings-server';

import { TeamMembersManager } from '../-components/team-members-manager';
import { browseTeamsQueryOptions } from '../-utils/organization-settings-query';
import { setTeamsPrivacy } from '../-utils/organization-settings-server';
import { workspaceSettingsContextQueryOptions } from '../-utils/workspace-context-query';
import { SettingsSection } from '../../settings/-components/settings-section';
import { NewTeamModal } from '../../settings/-components/teams/new-team-modal';
import { SETTINGS_HELP } from '../../settings/-utils/help-articles';

export const Route = createFileRoute(
  '/researchers/_authenticated/organization-settings/organization/teams',
)({
  component: BrowseTeamsPage,
  head: () => ({ meta: [{ title: t`Browse teams` }] }),
});

function BrowseTeamsPage() {
  const { user, activeDashboard } = Route.useRouteContext();
  const organizationId = activeDashboard.organization ?? '';
  const queryClient = useQueryClient();
  const teams = useQuery(browseTeamsQueryOptions(organizationId));
  const workspace = useQuery(
    workspaceSettingsContextQueryOptions(
      user.id,
      organizationId,
      activeDashboard.team ?? '',
    ),
  );
  const [search, setSearch] = useState('');
  const [managing, setManaging] = useState<BrowseTeam | null>(null);
  const [isNewTeamOpen, setIsNewTeamOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (teams.data?.teams ?? []).filter(
      (team) => !needle || team.name.toLowerCase().includes(needle),
    );
  }, [teams.data, search]);

  const refresh = () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.workspaceSettings.all(),
    });

  const orgAdmin =
    workspace.data?.roles.orgOwner || workspace.data?.roles.orgAdmin || false;

  return (
    <SettingsSection
      title={t`Browse teams`}
      description={t`Every team in your organization you can see, and who's on them.`}
      helpUrl={SETTINGS_HELP.teams}
    >
      {teams.isError ? (
        <AlertFloating
          color="error"
          title={t`We couldn't load the organization's teams`}
          description={t`Refresh the page to try again.`}
          confirmLabel=""
        />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Input
              aria-label={t`Find teams`}
              placeholder={t`Find teams in your organization`}
              value={search}
              onChange={setSearch}
              size="xs"
              className="max-w-xs"
            />
            <div className="flex items-center gap-2">
              {teams.data?.canChangePrivacy && (
                <div className="flex items-center gap-1">
                  <p className="text-sm text-tertiary">
                    {teams.data.privateTeams ? (
                      <Trans>Teams visibility: private</Trans>
                    ) : (
                      <Trans>Teams visibility: public</Trans>
                    )}
                  </p>
                  <Button
                    color="tertiary"
                    size="sm"
                    iconLeading={Edit03}
                    aria-label={t`Edit teams visibility`}
                    onClick={() => setIsPrivacyOpen(true)}
                  />
                </div>
              )}
              <Button
                color="secondary"
                size="sm"
                onClick={() => setIsNewTeamOpen(true)}
              >
                <Trans>New team</Trans>
              </Button>
            </div>
          </div>

          {teams.isPending ? (
            <div className="h-48 animate-pulse rounded-lg bg-secondary" />
          ) : visible.length === 0 ? (
            <p className="py-6 text-center text-sm text-tertiary">
              <Trans>No teams match that search.</Trans>
            </p>
          ) : (
            <ul className="divide-y divide-secondary">
              {visible.map((team) => (
                <li key={team.id} className="flex items-center gap-3 py-3">
                  <Avatar size="md" src={team.imageUrl} alt={team.name} />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-semibold text-primary">
                        {team.name}
                      </p>
                      <Badge type="pill-color" color="gray" size="sm">
                        {team.memberCount === 1
                          ? t`1 member`
                          : t`${team.memberCount} members`}
                      </Badge>
                    </div>
                    {team.description && (
                      <p className="truncate text-sm text-tertiary">
                        {team.description}
                      </p>
                    )}
                  </div>
                  {team.canAddMembers || team.canManageMembers ? (
                    <Button
                      color="secondary"
                      size="sm"
                      onClick={() => setManaging(team)}
                    >
                      {team.canManageMembers ? (
                        <Trans>Manage members</Trans>
                      ) : (
                        <Trans>Add members</Trans>
                      )}
                    </Button>
                  ) : (
                    team.ownerName && (
                      <p className="text-sm text-tertiary">
                        <Trans>Owner: {team.ownerName}</Trans>
                      </p>
                    )
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {managing && (
        <ControlledModal
          isOpen
          onOpenChange={(open) => {
            if (!open) {
              setManaging(null);
              void refresh();
            }
          }}
          centered
          title={t`${managing.name} members`}
          className="mx-auto w-full max-w-2xl rounded-xl bg-primary p-6 shadow-xl"
        >
          <div className="flex w-full flex-col gap-4">
            <h2 className="text-lg font-semibold text-primary">
              <Trans>{managing.name} members</Trans>
            </h2>
            <TeamMembersManager teamId={managing.id} />
            <div className="flex justify-end">
              <Button
                color="secondary"
                size="md"
                onClick={() => {
                  setManaging(null);
                  void refresh();
                }}
              >
                <Trans>Done</Trans>
              </Button>
            </div>
          </div>
        </ControlledModal>
      )}

      <NewTeamModal
        isOpen={isNewTeamOpen}
        onOpenChange={setIsNewTeamOpen}
        organizationId={organizationId}
        organizationName={workspace.data?.organization.name ?? ''}
        isOrganizationAdmin={orgAdmin}
        onCreated={() => void refresh()}
      />

      {isPrivacyOpen && teams.data && (
        <TeamsPrivacyModal
          privateTeams={teams.data.privateTeams}
          onClose={() => setIsPrivacyOpen(false)}
          onSaved={() => void refresh()}
        />
      )}
    </SettingsSection>
  );
}

function TeamsPrivacyModal({
  privateTeams,
  onClose,
  onSaved,
}: {
  privateTeams: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [value, setValue] = useState(privateTeams ? 'private' : 'public');
  const save = useMutation({
    mutationFn: async () => {
      const result = await setTeamsPrivacy({
        data: { privateTeams: value === 'private' },
      });
      if (!result.success) {
        throw new Error(result.message ?? t`Please try again.`);
      }
    },
    onSuccess: () => {
      notify({ title: t`Team privacy updated`, color: 'success' });
      onSaved();
      onClose();
    },
    onError: (error) =>
      notify({
        title: t`Couldn't update team privacy`,
        description: error.message,
        color: 'error',
      }),
  });

  return (
    <ControlledModal
      isOpen
      onOpenChange={(open) => !open && onClose()}
      centered
      title={t`Team privacy`}
      className="mx-auto w-full max-w-md rounded-xl bg-primary p-6 shadow-xl"
    >
      <div className="flex w-full flex-col gap-5">
        <h2 className="text-lg font-semibold text-primary">
          <Trans>Team privacy</Trans>
        </h2>
        <RadioGroup
          aria-label={t`Team privacy`}
          value={value}
          onChange={setValue}
        >
          <RadioButton
            value="public"
            label={t`Public`}
            hint={t`Everyone can see all teams in the organization.`}
          />
          <RadioButton
            value="private"
            label={t`Private`}
            hint={t`Only organization owners and admins can see all teams. Everyone else only sees teams they're on.`}
          />
        </RadioGroup>
        <div className="flex justify-end gap-3">
          <Button color="secondary" size="md" onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            color="primary"
            size="md"
            isLoading={save.isPending}
            isDisabled={(value === 'private') === privateTeams}
            onClick={() => save.mutate()}
          >
            <Trans>Save</Trans>
          </Button>
        </div>
      </div>
    </ControlledModal>
  );
}
